/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { FilterObject, FiltersValues } from '@types'

import { PAGINATION_KEYS } from '../constants'
import searchParamsToObject from '../utils/search-params-to-object'

const { PAGE, LIMIT } = PAGINATION_KEYS

export const defaultFiltersProviderValues: FiltersValues = {
  filters: { page: PAGE, limit: LIMIT },
  setFilters: () => null,
  handleFilters: () => null,
  handleResetFilters: () => null,
  resetFilters: 0,
  displayFilters: false,
  filtersToShow: undefined,
  handleFilterChange: () => () => {},
}
const FiltersContextV2 = React.createContext<FiltersValues>(defaultFiltersProviderValues)

type FiltersProviderV2Props = {
  children: React.ReactNode
  customized?: FilterObject
}

export const FiltersProviderV2 = ({ children, customized }: FiltersProviderV2Props) => {
  const { pathname, search } = useLocation()
  const [filters, setFilters] = useState({
    ...defaultFiltersProviderValues.filters,
    ...customized,
  })
  const [resetFilters, setResetFilters] = useState<number>(0)
  const [displayFilters, setDisplayFilters] = useState<boolean>(
    defaultFiltersProviderValues.displayFilters,
  )
  const [filtersToShow, setFiltersToShow] = useState<FilterObject>()
  const handleFilters: FiltersValues['handleFilters'] = ({ key, value, pageReset }) => {
    setFilters(state =>
      value !== undefined && value !== null
        ? {
            ...state,
            [key]: value,
            ...(pageReset && { page: defaultFiltersProviderValues.filters.page }),
          }
        : {
            ...Object.fromEntries(Object.entries(state).filter(item => item[0] !== key)),
            ...(pageReset && { page: defaultFiltersProviderValues.filters.page }),
          },
    )
  }

  const handleFilterChange: FiltersValues['handleFilterChange'] =
    (key, pageReset = true) =>
    value => {
      handleFilters({ key, value, pageReset })
    }

  const handleResetFilters = () => setResetFilters(state => state + 1)

  useEffect(() => {
    setFilters({ ...defaultFiltersProviderValues.filters, ...customized }) // reset filters
  }, [pathname, resetFilters])

  useEffect(() => {
    if (filtersToShow) {
      setDisplayFilters(!!Object.keys(filtersToShow).length)
    } else {
      setDisplayFilters(false)
    }
  }, [filtersToShow])

  useEffect(() => {
    const { page, pageSize, sort, sortBy, ...rest } = filters
    if (rest) {
      setFiltersToShow(rest)
    } else {
      setFiltersToShow(undefined)
    }
  }, [filters])

  useEffect(() => {
    setFilters(state => ({
      ...searchParamsToObject(search),
      ...state,
    }))
  }, [search])

  const values = {
    filters,
    setFilters,
    handleFilters,
    handleResetFilters,
    resetFilters,
    displayFilters,
    filtersToShow,
    handleFilterChange,
  }

  return <FiltersContextV2.Provider value={values}>{children}</FiltersContextV2.Provider>
}

export function useFiltersV2(): FiltersValues {
  const context = useContext(FiltersContextV2)
  if (!context) throw Error('This hook can only be used under the FiltersProviderV2 component')
  return context
}
